import { useFlags } from "flagsmith/react";
import React, { PropsWithChildren, ReactElement } from "react";
import { Redirect } from "react-router-dom";

import { SIDEBAR_USER_FLAGS } from "@utils/featureFlags";

import ROUTES from "@config/routes";
import { useAuth } from "@context/auth/AuthContext";
import { Roles } from "@shared/model";
import {
  ROLE_CARRIER,
  ROLE_CIF_DRIVER,
} from "@components/Routes/TypesPermissions";
import { isLegacyProfileOnlyDriver } from "@utils/permissions/legacyPermissionChecks";
import { isDriverOnlyProfile } from "@utils/permissions/permissionChecks";
import { useSystemModulesAuthFlag } from "@hooks/useSystemModulesAuthFlag";
import { AccessDenialReason, logAccessDenial } from "@utils/accessDenialLog";

interface RouteGuardProps extends PropsWithChildren<{}> {
  children: ReactElement;
  rules?: (_: string) => boolean;
  flag?: string;
  /**
   * SP-1315: quando true, bloqueia o acesso de perfis motorista puro (V2)
   * à rota, redirecionando para "Agenda e Vem". carrier tem precedência e
   * continua com acesso.
   */
  blockDriverOnlyProfile?: boolean;
}

const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  rules,
  flag,
  blockDriverOnlyProfile = false,
}) => {
  const { flag_for_access_management } = useFlags([
    "flag_for_access_management",
  ]);
  const useSystemModulesAuth = useSystemModulesAuthFlag();

  const flags = useFlags(SIDEBAR_USER_FLAGS);

  const { permissionSet } = useAuth();
  const isAdmin = permissionSet.ROLES.some(
    (role) => role === Roles.ADMIN || role === Roles.ADMINPORTAL
  );

  // Motorista (CIF) e Transportadora compartilham redirect para /agenda-e-vem
  const IS_DRIVER_OR_CARRIER_PROFILE = useSystemModulesAuth
    ? permissionSet.ROLES.includes(ROLE_CIF_DRIVER) ||
      permissionSet.ROLES.includes(ROLE_CARRIER)
    : isLegacyProfileOnlyDriver(permissionSet.SYSTEM_MODULES);

  const hasAccessProfileDriver = () => {
    return flag_for_access_management.enabled && IS_DRIVER_OR_CARRIER_PROFILE;
  };

  const hasAccess = () => {
    if (!flag_for_access_management.enabled) {
      return true;
    }

    const isAuthorized = permissionSet.SYSTEM_MODULES.some((authPermission) => {
      // rules=undefined é intencionalmente permissivo neste contexto.
      // No legado, rotas admin não possuíam RouteGuard algum — a proteção era
      // exclusivamente via ProfileRoute + adminMode. Com flag OFF, o comportamento
      // é idêntico ao anterior. Com flag ON, todas as rotas admin passam a exigir
      // módulos admin:* explícitos via getRouteGuardRules.
      const isRules = rules ? rules(authPermission) : true;
      const isFlag = Boolean(flag) ? flag && flags[flag].enabled : true;

      return isRules && isFlag;
    });

    return isAuthorized;
  };

  // SP-1315: bloqueia perfil motorista puro (V2) antes de liberar os children,
  // redirecionando para "Agenda e Vem". Admin e carrier não são afetados.
  const isBlockedDriverOnly =
    blockDriverOnlyProfile &&
    useSystemModulesAuth &&
    !isAdmin &&
    isDriverOnlyProfile(permissionSet?.ROLES ?? []);

  if (isBlockedDriverOnly) {
    // SP-1315: redirect por perfil motorista puro. Instrumentado para
    // distinguir, no Datadog, desta regra do caminho pré-existente abaixo.
    logAccessDenial({
      reason: AccessDenialReason.DRIVER_ONLY_PROFILE_BLOCKED,
      redirectTo: ROUTES.USER_ROUTES.ROADLOAD,
    });
    return <Redirect to={ROUTES.USER_ROUTES.ROADLOAD} />;
  }

  if (isAdmin || hasAccess()) {
    return children;
  }

  if (hasAccessProfileDriver()) {
    // Regra pré-existente: perfil motorista (CIF)/transportadora.
    logAccessDenial({
      reason: AccessDenialReason.DRIVER_OR_CARRIER_PROFILE_REDIRECT,
      redirectTo: ROUTES.USER_ROUTES.ROADLOAD,
    });
    return <Redirect to={ROUTES.USER_ROUTES.ROADLOAD} />;
  }

  return <Redirect to={ROUTES.USER_ROUTES.NOTFOUND} />;
};

export default RouteGuard;
